// Řádek „Hodí se k tomu" pod položkou košíku — 1–2 kusy příslušenství z nativních
// Souvisejících, nejvýš 3 řádky na košík. Náhrada košíkových nabídek placeného
// doplňku „Upsell a cross sell" (rozhodnutí Tomáše 2026-09-21, plán
// `~/.claude/plans/na-eshopu-m-me-aktivn-agile-oasis.md`, sondy fáze 0
// `ops/sweeps/2026-09-21-kosik-cross-sell-faze0/zaznam.md`).
//
// Zdroj = serverový `tr.related.related-to-<priceId>` téže stránky, nic se
// nefetchuje: cena sedí s ceníkem přihlášeného zákazníka, nevzniká CLS.
// Výběr (sklad, cenový strop, dedup, stropy) dělá čistý `cartCrossSellModel.js`.
// Plný seznam zůstává pod rozbalením „Související produkty (N)" a jeho nativní
// tlačítka přidávají stejně tiše (`bindSilentRelated`).
//
// Přidání jde přes `shoptet.cartShared.addToCart({priceId}, true)` — tichý
// režim bez popupu i bez našeho modalu, košík se přenačte sám (cesta A sondy
// 0.1). ⛔ Tlačítko NESMÍ nést `.add-to-cart-button`: capture listener
// `cartModal.js` by klik chytil a otevřel modal „Přidáno do košíku".
//
// Idempotentní: `initCart()` běží po každém AJAX přepočtu i z observeru.
// Podpis plánu drží tabulka (`data-bs-xs-sig`) — beze změny se nic
// nepřekresluje (klik ani fokus nezmizí), AJAX výměna `.cart-inner` podpis
// zahodí spolu s tabulkou a řádky se postaví znovu.

import { icon } from './icon.js';
import { money, NBSP } from './money.js';
import { addToCart } from '../shoptet-hooks.js';
import { ga4Event } from './ga4.js';
import { CART_XSELL } from '../locale/cs/common.js';
import { pickCrossSell, planSignature } from './cartCrossSellModel.js';
import { featureOn } from './features.js';

export const XS_CLASS = 'bs-co-xs';
const PER_ITEM = 2;
const PER_CART = 3;
// pojistka, kdyby se košík po přidání nepřestavěl (výpadek sítě, chyba serveru)
const BUSY_MS = 6000;
// tlačítka, která přidávají tiše: náš řádek + nativní karty rozbalených Souvisejících
const ADD_SEL = '.' + XS_CLASS + '__add, tr.related .add-to-cart-button';

// Jedno přidání najednou: během čekání na přestavbu košíku jsou všechna
// tlačítka řádků mimo hru (dvojklik ani druhý řádek nepošlou nic navíc).
let busy = null;
// `view` jde do GA4 jednou za pageview na dvojici spouštěč × nabídka, ne při
// každém AJAX přepočtu košíku.
const viewed = new Set();

function el(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function textOf(n) {
  return n ? n.textContent.replace(/\s+/g, ' ').trim() : '';
}

// „2 396 Kč" / „1 857,02 Kč" → číslo (`\s` bere i pevnou mezeru)
function parsePrice(text) {
  const m = String(text || '')
    .replace(/\s/g, '')
    .match(/-?\d+(?:[.,]\d+)?/);
  return m ? parseFloat(m[0].replace(',', '.')) : NaN;
}

/** Karty nativního panelu souvisejících → kandidáti pro model. */
export function readCandidates(related) {
  if (!related) return [];
  const out = [];
  for (const card of related.querySelectorAll('.cart-related-product')) {
    // variantní karta formulář nemá → bez priceId ji model vyřadí
    const form = card.querySelector('form.pr-action');
    const input = form && form.querySelector('input[name="priceId"]');
    const nameEl = card.querySelector('.cart-related-name');
    const img = card.querySelector('.cart-related-img img');
    out.push({
      priceId: input ? input.value : '',
      price: parsePrice(textOf(card.querySelector('.price-final'))),
      stock: textOf(card.querySelector('.cart-related-availability')),
      name: textOf(nameEl),
      href: nameEl ? nameEl.getAttribute('href') : '',
      img: img ? img.getAttribute('data-src') || img.getAttribute('src') || '' : '',
    });
  }
  return out;
}

// Položka, pod kterou se smí nabízet: běžný produkt (u služby fvStudio je
// `.main-link` SPAN, dárek nese vlastní řádek) s cenou za kus.
function readItem(row, relatedFor) {
  if (row.classList.contains('free-gift-row')) return null;
  const link = row.querySelector('td.p-name .main-link');
  if (!link || link.tagName !== 'A') return null;
  const input = row.querySelector('input[name="priceId"]');
  if (!input || !input.value) return null;
  return {
    key: input.value,
    sku: row.getAttribute('data-micro-sku') || '',
    unitPrice: parsePrice(textOf(row.querySelector('td.p-price .price-final'))),
    candidates: readCandidates(relatedFor(row)),
    row: row,
  };
}

function inCartPriceIds(rows) {
  const ids = [];
  for (const row of rows) {
    const input = row.querySelector('input[name="priceId"]');
    if (input && input.value) ids.push(input.value);
  }
  return ids;
}

function releaseBusy() {
  if (!busy) return;
  clearTimeout(busy.timer);
  for (const btn of document.querySelectorAll(ADD_SEL)) {
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }
  busy = null;
}

function canAdd() {
  const cs = window.shoptet && window.shoptet.cartShared;
  return !!cs && typeof cs.addToCart === 'function';
}

// `list`: `row` = náš řádek, `related` = rozbalené nativní Související
function onAdd(btn, priceId, sku, position, list) {
  if (busy) return;
  if (!addToCart({ priceId: priceId }, true)) return;
  busy = { btn: btn, timer: setTimeout(releaseBusy, BUSY_MS) };
  for (const other of document.querySelectorAll(ADD_SEL)) other.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  ga4Event('cart_xsell_add', { trigger_sku: sku, price_id: priceId, position: position, list: list });
}

// Rozbalené nativní „Související produkty (N)" přidávají stejně tiše jako řádek
// (rozhodnutí Tomáše 2026-09-21): jinak by default JS otevřel popup a
// cartModal.js náš modal „Přidáno do košíku" nad košíkem (cesta C sondy 0.1).
// Capture na WINDOW běží dřív než capture listener cartModal.js na documentu
// a `stopPropagation` ho k němu nepustí; `preventDefault` zastaví submit formu.
// Bez `cartShared` (offline, výpadek) se klik nechá nativní cestě.
let relatedBound = false;
function bindSilentRelated() {
  if (relatedBound) return;
  relatedBound = true;
  window.addEventListener(
    'click',
    function (event) {
      const t = event.target;
      if (!(t instanceof Element)) return;
      const btn = t.closest('tr.related .add-to-cart-button');
      if (!btn || !document.documentElement.classList.contains('bs-co-step-cart')) return;
      const input = btn.closest('form') && btn.closest('form').querySelector('input[name="priceId"]');
      if (!input || !input.value || !canAdd()) return;
      event.preventDefault();
      event.stopPropagation();
      const rel = btn.closest('tr.related');
      const m = /related-to-(\d+)/.exec(rel.className);
      const owner = m ? document.querySelector('tr.removeable input[name="priceId"][value="' + m[1] + '"]') : null;
      const ownerRow = owner && owner.closest('tr');
      const cards = [...rel.querySelectorAll('.cart-related-product')];
      onAdd(btn, input.value, ownerRow ? ownerRow.getAttribute('data-micro-sku') || '' : '', cards.indexOf(btn.closest('.cart-related-product')) + 1, 'related');
    },
    true,
  );
}

function buildOffer(pick, sku, position) {
  const li = el('li', XS_CLASS + '__item');
  li.dataset.bsPriceId = pick.priceId;

  const thumb = el('a', XS_CLASS + '__img');
  thumb.href = pick.href;
  thumb.tabIndex = -1;
  thumb.setAttribute('aria-hidden', 'true');
  if (pick.img) {
    const img = el('img');
    img.src = pick.img;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 40;
    img.height = 40;
    thumb.appendChild(img);
  } else {
    thumb.appendChild(icon('photo'));
  }
  li.appendChild(thumb);

  // ploché potomky: desktop = fotka | název nad „Skladem" | cena | Přidat,
  // mobil = fotka | název přes dva sloupce, pod ním „Skladem" · cena | Přidat
  // (rozvržení řeší grid-template-areas v _checkout.scss)
  const name = el('a', XS_CLASS + '__name', pick.name);
  name.href = pick.href;
  li.appendChild(name);
  li.appendChild(el('span', XS_CLASS + '__stock', CART_XSELL.stock));
  li.appendChild(el('span', XS_CLASS + '__price', '+' + NBSP + money(pick.price)));

  const btn = el('button', XS_CLASS + '__add');
  btn.type = 'button';
  btn.setAttribute('aria-label', CART_XSELL.addAria + pick.name);
  btn.appendChild(icon('plus'));
  btn.appendChild(el('span', null, CART_XSELL.add));
  if (busy) btn.disabled = true;
  btn.addEventListener('click', function (event) {
    event.preventDefault();
    onAdd(btn, pick.priceId, sku, position, 'row');
  });
  li.appendChild(btn);
  return li;
}

function buildRow(plan) {
  const tr = el('tr', XS_CLASS);
  tr.dataset.bsXsFor = plan.key;
  const td = el('td');
  const box = el('div', XS_CLASS + '__box');
  box.appendChild(el('div', XS_CLASS + '__title', CART_XSELL.title));
  const list = el('ul', XS_CLASS + '__list');
  plan.picks.forEach(function (pick, i) {
    list.appendChild(buildOffer(pick, plan.sku, i + 1));
  });
  box.appendChild(list);
  td.appendChild(box);
  tr.appendChild(td);
  return tr;
}

function rowsInPlace(table, plan) {
  const present = table.querySelectorAll('tr.' + XS_CLASS);
  if (present.length !== plan.length) return false;
  for (const row of plan) {
    const xs = row.item.row.nextElementSibling;
    if (!xs || !xs.classList.contains(XS_CLASS) || xs.dataset.bsXsFor !== row.key) return false;
  }
  return true;
}

/**
 * Postaví / obnoví řádky „Hodí se k tomu" pod položkami košíku.
 * `rows` = `tr.removeable` tabulky, `relatedFor(row)` = nativní panel
 * souvisejících té položky (`checkoutPage.js relatedRowFor`).
 */
export function renderCartCrossSell(rows, relatedFor) {
  // vypnuto (neznámý projekt, features.js) = žádný řádek a rozbalené Související
  // přidávají nativně (popup → modal „Přidáno do košíku")
  if (!featureOn('cartXsell')) return;
  bindSilentRelated();
  rows = [].slice.call(rows || []);
  const table = rows.length ? rows[0].closest('table') : null;
  if (busy && !busy.btn.isConnected) releaseBusy();
  if (!table) return;

  const items = [];
  for (const row of rows) {
    const item = readItem(row, relatedFor);
    if (item) items.push(item);
  }
  const byKey = new Map(
    items.map(function (it) {
      return [it.key, it];
    }),
  );
  const plan = pickCrossSell(items, inCartPriceIds(rows), { perItem: PER_ITEM, perCart: PER_CART }).map(function (p) {
    return { key: p.key, sku: p.sku, picks: p.picks, item: byKey.get(p.key) };
  });
  const sig = planSignature(plan);
  if (table.dataset.bsXsSig === sig && rowsInPlace(table, plan)) return;

  for (const old of table.querySelectorAll('tr.' + XS_CLASS)) old.remove();
  table.dataset.bsXsSig = sig;
  for (const p of plan) {
    const anchor = p.item.row;
    anchor.parentNode.insertBefore(buildRow(p), anchor.nextSibling);
    p.picks.forEach(function (pick, i) {
      const id = p.sku + '>' + pick.priceId;
      if (viewed.has(id)) return;
      viewed.add(id);
      ga4Event('cart_xsell_view', { trigger_sku: p.sku, price_id: pick.priceId, position: i + 1 });
    });
  }
}
